import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { X, Trash2, Timer, Bookmark, GripVertical, Share2, Scissors } from 'lucide-react'
import { useRoadStore, trimKeyFor } from '@/lib/roads/store'
import {
  formatDuration,
  roadDurationSeconds,
  trimKeptFraction,
  trimScaledDurationSeconds,
  useDurationsVersion,
} from '@/lib/roads/api'
import { useAuth } from '@/lib/auth/useAuth'
import { onSavedRoutesChanged, saveRouteNamed, unsaveRoute, fetchSavedRoutes } from '@/lib/auth/auth'
import { routeShareUrl, copyText } from '@/lib/roads/share'
import RouteNameModal from '@/components/panels/RouteNameModal'
import { useAuthUi } from '@/lib/auth/authUi'
import { trimActive } from '@/lib/roads/trim'
import { findRouteLayoutIssues } from '@/lib/roads/ride'
import { buildRouteMapsUrls } from '@/lib/roads/ride'
import { useRouteSummary } from '@/lib/roads/useRouteSummary'
import RouteLinkModal from '@/components/panels/RouteLinkModal'
import BikeTypeTip from '@/components/ui/BikeTypeTip'

const BIKE_DOTS: Record<string, string> = {
  Sport: '#FF4438',
  ADV: '#34D26B',
  Sumo: '#FF9F1C',
  Cruiser: '#3B82F6',
}

export default function RouteTray() {
  // Road travel times resolve asynchronously — subscribe so totals update
  // live as each road's OSRM duration lands.
  useDurationsVersion()
  const roads = useRoadStore((s) => s.roads)
  const routeIds = useRoadStore((s) => s.routeIds)
  const routeName = useRoadStore((s) => s.routeName)
  const setRouteName = useRoadStore((s) => s.setRouteName)
  const routeSavedBy = useRoadStore((s) => s.routeSavedBy)
  const routeTrims = useRoadStore((s) => s.routeTrims)
  const setRouteSavedBy = useRoadStore((s) => s.setRouteSavedBy)
  const openStats = useRoadStore((s) => s.openStats)
  const selectRoad = useRoadStore((s) => s.selectRoad)
  const removeFromRoute = useRoadStore((s) => s.removeFromRoute)
  const reorderRoute = useRoadStore((s) => s.reorderRoute)
  const clearRoute = useRoadStore((s) => s.clearRoute)
  const setTrimmingRoad = useRoadStore((s) => s.setTrimmingRoad)
  const focusRoute = useRoadStore((s) => s.focusRoute)
  const setRouteHoverKey = useRoadStore((s) => s.setRouteHoverKey)
  const ride = useRoadStore((s) => s.ride)
  const { session, ready, profile } = useAuth()
  const openAuthModal = useAuthUi((s) => s.openAuthModal)

  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'removing' | 'removed' | 'error'>('idle')
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveToast, setSaveToast] = useState<string | null>(null)
  const saveToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // UUID of the server row this route is saved as (the route's primary
  // identity) — save, share and unsave all operate through it.
  const [savedRouteId, setSavedRouteId] = useState<string | null>(null)

  const flashSaveToast = (msg: string) => {
    if (saveToastTimer.current) clearTimeout(saveToastTimer.current)
    setSaveToast(msg)
    saveToastTimer.current = setTimeout(() => setSaveToast(null), 1600)
  }
  const [linkModal, setLinkModal] = useState<{ open: boolean; urls: string[] }>({ open: false, urls: [] })
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)

  // Touch reorder: long-press the row (or its grip), then drag vertically.
  // touch-action: none on the grip prevents the page scrolling the list mid-drag.
  const touchDrag = useRef<{ id: number; y: number; index: number } | null>(null)
  const touchMoved = useRef(false)
  const touchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const touchFromRef = useRef<number | null>(null)

  const clearTouchState = () => {
    if (touchTimer.current) clearTimeout(touchTimer.current)
    touchTimer.current = null
    touchDrag.current = null
    touchFromRef.current = null
    setDragIndex(null)
    setDropIndex(null)
  }

  const onTouchRowStart = (e: React.TouchEvent, i: number) => {
    const touch = e.touches[0]
    if (!touch) return
    touchMoved.current = false
    touchFromRef.current = i
    touchDrag.current = null
    touchTimer.current = setTimeout(() => {
      touchDrag.current = { id: touch.identifier, y: touch.clientY, index: i }
      setDragIndex(i)
      if (navigator.vibrate) navigator.vibrate(20)
    }, 300)
  }

  // After a touch drag the browser still synthesizes a click on the row —
  // swallow it so reordering doesn't also select the road.
  const suppressNextClick = () => {
    if (touchDrag.current) {
      touchFromRef.current = null
      return true
    }
    return false
  }

  const onTouchRowMove = (e: React.TouchEvent, i: number) => {
    touchMoved.current = true
    const d = touchDrag.current
    if (!d) return
    const touch = Array.from(e.touches).find((t) => t.identifier === d.id)
    if (!touch) return
    const dy = touch.clientY - d.y
    // Each row is roughly 40px tall — swap indices as the finger crosses rows.
    const steps = Math.round(dy / 40)
    const target = Math.max(0, Math.min(routeRoads.length - 1, d.index + steps))
    if (target !== dropIndex) setDropIndex(target)
  }

  const onTouchRowEnd = (e: React.TouchEvent, i: number) => {
    const d = touchDrag.current
    if (d) {
      if (dropIndex !== null && dropIndex !== i) reorderRoute(i, dropIndex)
      clearTouchState()
    } else if (touchTimer.current) {
      clearTimeout(touchTimer.current)
      touchTimer.current = null
    }
  }

  async function handleStartRide() {
    const urls = await buildRouteMapsUrls(routeRoads, routeTrims)
    if (urls.length) setLinkModal({ open: true, urls })
  }

  // Reset back to the default Save button whenever the route changes or the
  // auth session changes — "Saved" only ever reflects THIS route.
  useEffect(() => {
    setSaveState('idle')
    setSavedRouteId(null)
    setRouteSavedBy(null)
    if (saveToastTimer.current) clearTimeout(saveToastTimer.current)
    setSaveToast(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeIds.join('>'), session?.user.id])

  // Hydrate: when a loaded route is one of the signed-in user's saved
  // routes, restore its UUID and "Saved" state (and its name, if the tray
  // doesn't already show one). Matched by UUID-backed road list first.
  useEffect(() => {
    if (!session || routeRoads.length === 0 || saveState === 'saved') return
    let cancelled = false
    void fetchSavedRoutes().then((rows) => {
      if (cancelled) return
      const match =
        rows.find(
          (r) =>
            r.road_ids.length === routeIds.length &&
            r.road_ids.every((id, i) => id === routeIds[i]),
        ) ?? (routeName ? rows.find((r) => r.name === routeName) : undefined)
      if (match) {
        setSavedRouteId(match.id)
        setSaveState('saved')
        if (match.name && !routeName) setRouteName(match.name)
        setRouteSavedBy((match as { saved_by_username?: string | null }).saved_by_username ?? profile?.username ?? 'Anon')
      }
    })
    return () => {
      cancelled = true
    }
  }, [session, routeIds.join('>'), routeName, saveState])

  // If this route is deleted elsewhere (e.g. the account panel), drop the
  // stale "Saved" state so the button reflects reality.
  useEffect(
    () =>
      onSavedRoutesChanged(() => {
        // Deletion elsewhere (account panel) invalidates the UUID reference —
        // drop it so save/share never target a deleted row.
        setSavedRouteId(null)
        setSaveState((s) => (s === 'saved' ? 'idle' : s))
      }),
    [],
  )

  useEffect(() => {
    return () => {
      if (saveToastTimer.current) clearTimeout(saveToastTimer.current)
    }
  }, [])

  // Share: copy this route's permanent public URL (/route/[id]). No sign-in
  // needed — the server keeps one shared row per distinct road list. Every
  // server-side route must be named first, so a small popup collects it —
  // except when the route is already saved and named: then the stored name
  // is used directly and no popup appears.
  const [sharing, setSharing] = useState(false)
  const [nameModal, setNameModal] = useState<'save' | 'share' | null>(null)

  async function handleShare() {
    if (saveState === 'saved' && savedRouteId && routeName) {
      const url = await shareSavedRoute(routeName)
      if (url) return
      // UUID share failed — fall through to the naming popup path.
    }
    setNameModal('share')
  }

  async function handleSave() {
    if (!session) {
      openAuthModal('save')
      return
    }
    if (saveState === 'saved') {
      // Already saved → tapping removes it from saved routes.
      setSaveState('removing')
      const { error } = await unsaveRoute(routeIds, savedRouteId)
      if (error && error !== 'not-signed-in') {
        setSaveError(error)
        setSaveState('error')
        return
      }
      setSaveState('removed')
      flashSaveToast('Unsaved')
      setTimeout(() => setSaveState((s) => (s === 'removed' ? 'idle' : s)), 1000)
      return
    }
    setNameModal('save')
  }

  // Sanity check before persisting: a big jump between two roads' waypoint
  // chains means one of them runs the wrong way (e.g. an accidental Reverse).
  async function layoutWarning(): Promise<string | null> {
    try {
      const issues = await findRouteLayoutIssues(routeRoads, routeTrims)
      if (issues.length) {
        const gap = issues[0].gapMiles
        return `Check the route order — there's a ${gap} mile gap between two roads, so one may be running the wrong way. Use Reverse in the trim panel if so.`
      }
    } catch {
      /* geometry unavailable — don't block saving on the check itself */
    }
    return null
  }

  // Called by the naming popup once a valid name is submitted. force=true
  // skips the layout warning (user chose "Save Anyway").
  async function handleNamedSave(name: string, force = false): Promise<string | null> {
    const layout = force ? null : await layoutWarning()
    if (layout) return layout
    setSaveState('saving')
    const { error, routeId } = await saveRouteNamed(routeIds, name, savedRouteId, routeTrims)
    if (error === 'duplicate') {
      setSaveState('idle')
      return 'duplicate'
    }
    if (error === 'not-signed-in') {
      setSaveState('idle')
      openAuthModal('save')
      return null
    }
    if (error) {
      setSaveError('Could not save this route.')
      setSaveState('error')
      return 'error'
    }
      if (routeId) {
        setSavedRouteId(routeId)
        setRouteSavedBy(profile?.username ?? 'Anon')
      }
    setSaveState('saved')
    setRouteName(name)
    flashSaveToast('Saved')
    return null
  }

  // Share a saved route through its UUID row, using its stored name.
  // Returns the copied URL on success, null when the UUID path can't be
  // used (caller falls back to the road-list share).
  async function shareSavedRoute(name: string): Promise<string | null> {
    if (!savedRouteId) return null
    setSharing(true)
    try {
      const url = await routeShareUrl(routeIds, name, savedRouteId, routeTrims)
      if (!url) return null
      if (await copyText(url)) {
        flashSaveToast('URL copied')
        return url
      }
      flashSaveToast('Copy blocked — try again')
      return url
    } finally {
      setSharing(false)
    }
  }

  async function handleNamedShare(name: string, force = false): Promise<string | null> {
    const layout = force ? null : await layoutWarning()
    if (layout) return layout
    setSharing(true)
    try {
      const url = await routeShareUrl(routeIds, name, savedRouteId, routeTrims)
      if (!url) {
        flashSaveToast('Link not ready — try again')
        return 'error'
      }
      if (await copyText(url)) {
        flashSaveToast('URL copied')
        // Anonymous shares must NOT flip the Save bookmark — "Saved" only
        // reflects routes saved to the signed-in user's account.
        if (session && savedRouteId) setSaveState('saved')
        return null
      }
      flashSaveToast('Copy blocked — try again')
      return 'error'
    } finally {
      setSharing(false)
    }
  }

  // Duplicates allowed: a road can appear multiple times, once per visit.
  const routeRoads = useMemo(
    () => routeIds.map((id) => roads.find((r) => r.id === id)).filter((r): r is NonNullable<typeof r> => !!r),
    [roads, routeIds],
  )

  // Existing names of the signed-in user's saved routes, for the popup's
  // instant duplicate-name warning (the database enforces it regardless).
  const [savedRouteNames, setSavedRouteNames] = useState<string[]>([])
  useEffect(() => {
    if (!session) {
      setSavedRouteNames([])
      return
    }
    void fetchSavedRoutes().then((rows) => setSavedRouteNames(rows.map((r) => r.name ?? '')))
  }, [session, routeIds.join('>')])

  // Button-row geometry: on wide panels the share button floats exactly
  // midway between the Save button's right edge and START RIDE's left edge
  // (START RIDE is centered, so its left edge is measured). On very narrow
  // rows (<340px) the two would collide, so share falls back to sitting
  // beside START RIDE.
  const trayRowRef = useRef<HTMLDivElement | null>(null)
  const startRideRef = useRef<HTMLButtonElement | null>(null)
  const [trayRowW, setTrayRowW] = useState<number | null>(null)
  const [startRideLeft, setStartRideLeft] = useState<number | null>(null)
  const hasRoute = routeRoads.length > 0
  useLayoutEffect(() => {
    if (!hasRoute) return
    const el = trayRowRef.current
    if (!el) return
    const update = () => {
      setTrayRowW(el.getBoundingClientRect().width)
      // The row is position:relative — offsetLeft is relative to it.
      setStartRideLeft(startRideRef.current?.offsetLeft ?? null)
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [hasRoute])
  const compactTray = trayRowW != null && trayRowW < 340

  // Trim-aware: each chip and the fallback totals describe the KEPT section of
  // a trimmed visit, not the full road.
  const keptFraction = (i: number) => trimKeptFraction(routeTrims[trimKeyFor(routeIds, i)])
  const totalMiles = routeRoads.reduce(
    (s, r, i) => s + (r.distance_miles ?? 0) * keptFraction(i),
    0,
  )
  const rated = routeRoads.map((r) => r.ratings?.overall).filter((v): v is number => v != null)
  const avg = rated.length ? (rated.reduce((a, b) => a + b, 0) / rated.length).toFixed(1) : null
  const totalSeconds = routeRoads.reduce(
    (s, r, i) => s + (trimScaledDurationSeconds(r, routeTrims[trimKeyFor(routeIds, i)]) ?? 0),
    0,
  )
  const fallbackTime = formatDuration(totalSeconds)
  // Accurate connected totals (one OSRM pass over the whole route, server-cached)
  // replace the per-road sums once they land. While they're being computed the
  // tray shows italic "computing…" placeholders instead of the rough sums.
  const { summary, computing } = useRouteSummary(routeRoads, routeTrims)
  const accurateTime = summary?.seconds != null ? formatDuration(summary.seconds) : null
  const accurateMiles = summary?.miles

  return (
    <>
      <AnimatePresence>
        {routeRoads.length > 0 && !ride && (
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ type: 'spring', damping: 28, stiffness: 280 }}
          data-route-tray
          className="fixed md:absolute z-40 bottom-3 inset-x-3 md:inset-x-auto md:right-3 md:w-[min(420px,calc(100vw-24px))]"
        >
          <div
            className="rounded-xl bg-[#0C0C0E]/95 backdrop-blur border border-white/10 p-3 space-y-2.5"
            data-save-error={saveError ?? undefined}
            onClick={(e) => {
              // Clicking anywhere on the panel that isn't a road chip (or a
              // button) frames the ENTIRE route on the map.
              if (e.target === e.currentTarget || (e.target as HTMLElement).closest('[data-route-summary]')) {
                focusRoute()
              }
            }}
          >
            <div
              data-route-summary
              className="flex items-center gap-3 flex-wrap cursor-pointer"
            >
              {routeName ? (
                <>
                  <span
                    className="text-[10px] font-mono uppercase tracking-widest text-amber-400
                      min-w-0 basis-full sm:basis-auto sm:min-w-[80px] sm:max-w-[50%] sm:shrink"
                    title={routeName}
                  >
                    {routeName}
                  </span>
                  {routeSavedBy && (
                    <span className="basis-full text-[10px] text-white/50 sm:basis-auto">
                      by{' '}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          openStats(routeSavedBy)
                        }}
                        className="cursor-pointer underline-offset-2 hover:underline"
                        title={`${routeSavedBy}'s stats`}
                      >
                        {routeSavedBy}
                      </button>
                    </span>
                  )}
                </>
              ) : (
                <span className="text-[10px] font-mono uppercase tracking-widest text-amber-400">
                  Your route
                </span>
              )}
              <span className="text-xs text-white/60">{routeRoads.length} roads</span>
              {accurateMiles != null ? (
                <span className="text-xs text-white/60">{Math.round(accurateMiles)} mi</span>
              ) : computing ? (
                <span className="text-xs text-white/40 italic">…</span>
              ) : (
                <span className="text-xs text-white/60">{Math.round(totalMiles)} mi</span>
              )}
              {accurateTime ? (
                <span className="text-xs text-white/60 flex items-center gap-1">
                  <Timer size={11} className="text-amber-400" /> {accurateTime}
                </span>
              ) : computing ? (
                <span className="text-xs text-white/40 italic flex items-center gap-1">
                  <Timer size={11} className="text-amber-400/50" /> …
                </span>
              ) : fallbackTime ? (
                <span className="text-xs text-white/60 flex items-center gap-1">
                  <Timer size={11} className="text-amber-400" /> {fallbackTime}
                </span>
              ) : null}
              {avg && (
                <span className="text-xs text-white/60">
                  avg <span className="text-amber-300 font-mono">{avg}</span>/10
                </span>
              )}
            </div>

            <div ref={trayRowRef} className="relative flex items-center justify-center h-11">
              {/* Save — pinned to the far left so START RIDE stays centered */}
              <div className="absolute left-0 h-11 w-11">
              <AnimatePresence>
                {saveToast && (
                  <motion.div
                    initial={{ opacity: 0, y: 6, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 6, scale: 0.9 }}
                    transition={{ duration: 0.15 }}
                    className="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 rounded-md bg-[#0C0C0E] border border-white px-2.5 py-1 text-[11px] font-medium text-white/90 shadow-lg pointer-events-none"
                  >
                    {saveToast}
                  </motion.div>
                )}
              </AnimatePresence>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  void handleSave()
                }}
                disabled={!ready || saveState === 'saving'}
                className={`h-11 w-11 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                  saveState === 'saved'
                    ? 'text-amber-400'
                    : saveState === 'removed' || saveState === 'error'
                      ? 'text-red-400'
                      : 'text-white/60 hover:text-amber-300'
                }`}
                title={
                  session
                    ? saveState === 'saved'
                      ? 'Saved — tap to remove'
                      : 'Save this route to your account'
                    : 'Sign in to save this route'
                }
              >
                {saveState === 'removed' ? null : (
                  <Bookmark
                    size={16}
                    className={saveState === 'saved' ? 'fill-amber-400' : undefined}
                  />
                )}
              </button>
              </div>
              {/* START RIDE — always exactly centered in the panel */}
              <button
                ref={startRideRef}
                onClick={(e) => {
                  e.stopPropagation()
                  void handleStartRide()
                }}
                className="h-11 px-5 rounded-lg bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm tracking-wide"
              >
                START RIDE
              </button>
              {/* Share — floats midway between Save and START RIDE on wide
                  rows; beside START RIDE when the panel is too narrow. */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  void handleShare()
                }}
                disabled={sharing}
                style={
                  !compactTray
                    ? {
                        // Midpoint between Save's right edge (44px) and
                        // START RIDE's measured left edge.
                        left:
                          startRideLeft != null
                            ? (44 + startRideLeft) / 2
                            : 'calc(50% - 55px)',
                        transform: 'translateX(-50%)',
                      }
                    : undefined
                }
                className={`h-11 w-11 flex items-center justify-center rounded-lg text-white/60 hover:text-amber-300 disabled:opacity-40 ${
                  compactTray ? 'ml-1.5' : 'absolute'
                }`}
                title="Copy share link"
              >
                <Share2 size={16} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  clearRoute()
                }}
                className="h-11 w-11 absolute right-0 flex items-center justify-center rounded-lg text-white/40 hover:text-red-400"
                title="Clear route"
              >
                <Trash2 size={16} />
              </button>
            </div>


            <div className="max-h-56 overflow-y-auto space-y-1.5 pr-0.5">
              {routeRoads.map((r, i) => {
                const t = formatDuration(
                  trimScaledDurationSeconds(r, routeTrims[trimKeyFor(routeIds, i)]),
                )
                // With duplicate visits allowed, the row key and every action
                // key on the road's OCCURRENCE (1st pass = plain id, later
                // passes = "id#2"…), never just the road id.
                const trimKey = trimKeyFor(routeIds, i)
                return (
                  <div
                    key={trimKey}
                    draggable
                    onDragStart={(e) => {
                      setDragIndex(i)
                      e.dataTransfer.effectAllowed = 'move'
                      e.dataTransfer.setData('text/plain', String(i))
                    }}
                    onDragOver={(e) => {
                      e.preventDefault()
                      e.dataTransfer.dropEffect = 'move'
                      setDropIndex(i)
                    }}
                    onDragLeave={() => setDropIndex((d) => (d === i ? null : d))}
                    onDrop={(e) => {
                      e.preventDefault()
                      if (dragIndex !== null && dragIndex !== i) reorderRoute(dragIndex, i)
                      setDragIndex(null)
                      setDropIndex(null)
                    }}
                    onDragEnd={() => {
                      setDragIndex(null)
                      setDropIndex(null)
                    }}
                    onTouchStart={(e) => onTouchRowStart(e, i)}
                    onTouchMove={(e) => onTouchRowMove(e, i)}
                    onTouchEnd={(e) => onTouchRowEnd(e, i)}
                    onTouchCancel={clearTouchState}
                    className={`flex items-center gap-2 rounded-lg bg-white/[0.04] border pl-2.5 pr-1 py-1.5 cursor-grab active:cursor-grabbing transition-colors ${
                      dragIndex === i
                        ? 'opacity-40'
                        : dropIndex === i
                          ? 'border-amber-400'
                          : 'border-white/10 hover:border-amber-400/40'
                    }`}
                    onClick={() => {
                      if (suppressNextClick()) return
                      // Pass this chip's visit key so the camera frames THAT
                      // visit's trimmed span (not the other visit's).
                      selectRoad(r.id, trimKey)
                    }}
                    onMouseEnter={() => setRouteHoverKey(trimKey)}
                    onMouseLeave={() => setRouteHoverKey(null)}
                  >
                    <span
                      className="touch-none p-1 -m-1 shrink-0 flex cursor-grab"
                      onTouchStart={(e) => onTouchRowStart(e, i)}
                    >
                      <GripVertical size={13} className="text-white/30" aria-hidden />
                    </span>
                    {/* Fixed width sized for the widest label (CRUISER) so the
                        road names stay vertically aligned across rows. */}
                    <div className="flex flex-col items-end gap-0.5 w-12 shrink-0">
                      {r.bike_types.slice(0, 4).map((b) => (
                        <BikeTypeTip key={b} type={b}>
                          <span
                            className="text-[9px] font-mono uppercase text-white/60 leading-tight cursor-default"
                          >
                            {b}
                            <span
                              className="block w-full h-[2px] mt-px rounded-full"
                              style={{ background: BIKE_DOTS[b] ?? '#888' }}
                            />
                          </span>
                        </BikeTypeTip>
                      ))}
                    </div>
                    <span className="text-xs text-white/80 truncate flex-1">{r.name}</span>
                    {t && <span className="text-[10px] font-mono text-white/50 shrink-0">{t}</span>}
                    <span className="text-[10px] font-mono text-amber-300 shrink-0">
                      {(r.ratings?.overall ?? 0).toFixed(1)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setTrimmingRoad(trimKey)
                      }}
                      className={`h-8 w-8 flex items-center justify-center rounded ${
                        trimActive(routeTrims[trimKey])
                          ? 'text-amber-400'
                          : 'text-white/40 hover:text-white'
                      }`}
                      title={`Trim this visit of ${r.name}`}
                      aria-label={`Trim this visit of ${r.name}`}
                    >
                      <Scissors size={13} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        removeFromRoute(i)
                      }}
                      className="h-8 w-8 flex items-center justify-center rounded text-white/40 hover:text-white"
                    >
                      <X size={13} />
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        </motion.div>
        )}
      </AnimatePresence>

      <RouteNameModal
        open={nameModal !== null}
        purpose={nameModal ?? 'save'}
        existingNames={savedRouteNames}
        onSubmit={nameModal === 'share' ? handleNamedShare : handleNamedSave}
        onClose={() => setNameModal(null)}
      />

      <RouteLinkModal
        open={linkModal.open}
        urls={linkModal.urls}
        miles={totalMiles}
        roadCount={routeRoads.length}
        onClose={() => setLinkModal({ open: false, urls: [] })}
      />
    </>
  )
}
